import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Server, ShieldCheck, CheckCircle2, KeyRound, Save, Users, Crown, Trash2, UserPlus, PenLine } from "lucide-react";
import { EmailTemplatesSection } from "@/components/EmailTemplatesSection";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";

const SIGNATURE_MODULES = {
  toolbar: [["bold", "italic", "underline"], [{ list: "bullet" }], ["clean"]],
};
const SIGNATURE_FORMATS = ["bold", "italic", "underline", "list"];

const Settings = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    from_email: "",
    from_name: "",
    use_tls: true,
    signature: "",
  });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await api.post("/settings/smtp/test");
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du test SMTP");
    } finally {
      setTestingSmtp(false);
    }
  };

  const [keys, setKeys] = useState({ anthropic_api_key: "", openai_api_key: "", plaud_api_key: "", calendly_api_key: "" });
  const [keyHints, setKeyHints] = useState({ anthropic: {}, openai: {}, plaud: {}, calendly: {} });
  const [savingKeys, setSavingKeys] = useState(false);

  const isAdmin = user?.role === "admin";
  const [accessUsers, setAccessUsers] = useState([]);
  const [newAccess, setNewAccess] = useState({ email: "", role: "membre" });
  const [addingAccess, setAddingAccess] = useState(false);

  const loadAccess = async () => {
    try {
      const res = await api.get("/admin/users");
      setAccessUsers(res.data);
    } catch (e) {}
  };

  const addAccess = async () => {
    if (!newAccess.email.trim()) {
      toast.error("Saisissez une adresse e-mail");
      return;
    }
    setAddingAccess(true);
    try {
      await api.post("/admin/users", newAccess);
      toast.success(`Accès accordé à ${newAccess.email}`);
      setNewAccess({ email: "", role: "membre" });
      loadAccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'ajout");
    } finally {
      setAddingAccess(false);
    }
  };

  const removeAccess = async (email) => {
    if (!window.confirm(`Retirer l'accès de ${email} ? Ses sessions seront déconnectées.`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      toast.success("Accès retiré");
      loadAccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    }
  };

  const load = async () => {
    try {
      const [res, kres] = await Promise.all([api.get("/settings/smtp"), api.get("/settings/keys")]);
      if (res.data.configured) {
        setConfigured(true);
        setForm((f) => ({
          ...f,
          host: res.data.host || "",
          port: res.data.port || 587,
          username: res.data.username || "",
          from_email: res.data.from_email || "",
          from_name: res.data.from_name || "",
          use_tls: res.data.use_tls,
          signature: res.data.signature || "",
          password: "",
        }));
      }
      setKeyHints(kres.data);
    } catch (e) {
      toast.error("Impossible de charger les paramètres");
    } finally {
      setLoading(false);
    }
  };

  const saveKeys = async () => {
    setSavingKeys(true);
    try {
      const res = await api.put("/settings/keys", keys);
      setKeyHints(res.data);
      setKeys({ anthropic_api_key: "", openai_api_key: "", plaud_api_key: "", calendly_api_key: "" });
      toast.success("Clés API enregistrées");
    } catch (e) {
      toast.error("Erreur d'enregistrement des clés");
    } finally {
      setSavingKeys(false);
    }
  };

  useEffect(() => {
    load();
    if (user?.role === "admin") loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const save = async () => {
    if (!form.host || !form.username || !form.password || !form.from_email) {
      toast.error("Serveur, identifiant, mot de passe et e-mail expéditeur sont requis");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/smtp", { ...form, port: Number(form.port) });
      setConfigured(true);
      toast.success("Paramètres SMTP enregistrés");
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (loading) {
    return (
      <AppLayout>
        <div className="h-40 bg-white/60 rounded-2xl border border-[#EAE6DF] animate-pulse" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#C07761]/10 text-[#C07761] flex items-center justify-center">
            <Server className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-[#2D2C28]">Paramètres SMTP</h1>
            <p className="text-[#757169] text-sm">
              Configurez votre serveur d'envoi pour transmettre les e-mails à vos patients.
            </p>
          </div>
        </div>

        {configured && (
          <div className="flex items-center gap-2 mt-5 text-sm text-[#5e7a6e] bg-[#7B968B]/10 rounded-xl p-3">
            <CheckCircle2 className="w-4 h-4" /> Un serveur SMTP est configuré.
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)] mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-[#575349]">Serveur SMTP *</Label>
              <Input
                data-testid="smtp-host-input"
                value={form.host}
                onChange={set("host")}
                placeholder="smtp.gmail.com"
                className="mt-1.5 bg-white rounded-xl"
              />
            </div>
            <div>
              <Label className="text-[#575349]">Port *</Label>
              <Input
                data-testid="smtp-port-input"
                type="number"
                value={form.port}
                onChange={set("port")}
                className="mt-1.5 bg-white rounded-xl"
              />
            </div>
          </div>

          <div>
            <Label className="text-[#575349]">Identifiant *</Label>
            <Input
              data-testid="smtp-username-input"
              value={form.username}
              onChange={set("username")}
              placeholder="votre.email@exemple.com"
              className="mt-1.5 bg-white rounded-xl"
            />
          </div>

          <div>
            <Label className="text-[#575349]">Mot de passe {configured && "(laisser vide pour conserver)"} *</Label>
            <Input
              data-testid="smtp-password-input"
              type="password"
              value={form.password}
              onChange={set("password")}
              placeholder="••••••••"
              className="mt-1.5 bg-white rounded-xl"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#575349]">E-mail expéditeur *</Label>
              <Input
                data-testid="smtp-from-email-input"
                value={form.from_email}
                onChange={set("from_email")}
                placeholder="cabinet@exemple.com"
                className="mt-1.5 bg-white rounded-xl"
              />
            </div>
            <div>
              <Label className="text-[#575349]">Nom expéditeur</Label>
              <Input
                data-testid="smtp-from-name-input"
                value={form.from_name}
                onChange={set("from_name")}
                placeholder="Dr. Marie Martin"
                className="mt-1.5 bg-white rounded-xl"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-[#FBF9F6] p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#7B968B]" />
              <span className="text-sm text-[#575349]">Utiliser STARTTLS (port 587). Désactivez pour SSL (port 465).</span>
            </div>
            <Switch
              data-testid="smtp-tls-switch"
              checked={form.use_tls}
              onCheckedChange={(v) => setForm({ ...form, use_tls: v })}
            />
          </div>

          <div>
            <Label className="text-[#575349] flex items-center gap-1.5">
              <PenLine className="w-4 h-4 text-[#7B968B]" /> Signature e-mail
            </Label>
            <p className="text-xs text-[#9c988f] mt-1 mb-2">
              Ajoutée automatiquement à la fin de chaque e-mail envoyé (nom, titre, coordonnées…).
            </p>
            <div className="email-editor signature-editor bg-white rounded-xl" data-testid="smtp-signature-input">
              <ReactQuill
                theme="snow"
                value={form.signature}
                onChange={(v) => setForm((f) => ({ ...f, signature: v }))}
                modules={SIGNATURE_MODULES}
                formats={SIGNATURE_FORMATS}
                placeholder="Victor Loher — Thérapeute TCC&#10;06 12 34 56 78 · suivi.alesia-tcc.fr"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-smtp-btn"
              className="flex-1 rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white h-11"
            >
              {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
            </Button>
            <Button
              variant="outline"
              onClick={testSmtp}
              disabled={testingSmtp}
              data-testid="test-smtp-btn"
              className="rounded-xl border-[#EAE6DF] text-[#575349] h-11 sm:w-56"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {testingSmtp ? "Test en cours…" : "Tester la connexion"}
            </Button>
          </div>
        </div>

        <p className="text-xs text-[#9c988f] mt-4 leading-relaxed">
          Astuce : pour Gmail, utilisez un « mot de passe d'application » (et non votre mot de passe
          habituel) avec le serveur smtp.gmail.com sur le port 587.
        </p>

        {/* API keys & integrations */}
        <div className="flex items-center gap-3 mt-12">
          <div className="w-11 h-11 rounded-xl bg-[#7B968B]/15 text-[#7B968B] flex items-center justify-center">
            <KeyRound className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-[#2D2C28]">Clés API & intégrations</h2>
            <p className="text-[#757169] text-sm">
              Vos clés sont utilisées en priorité pour la génération (recommandé HDS : les données patients vont directement chez le fournisseur). En prévisualisation, la clé Emergent sert de repli si aucune clé n'est renseignée.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)] mt-6 space-y-5">
          <KeyField
            testid="anthropic-key-input"
            label="Clé API Claude (Anthropic)"
            placeholder="sk-ant-..."
            value={keys.anthropic_api_key}
            hint={keyHints.anthropic}
            onChange={(v) => setKeys({ ...keys, anthropic_api_key: v })}
          />
          <KeyField
            testid="openai-key-input"
            label="Clé API GPT (OpenAI)"
            placeholder="sk-..."
            value={keys.openai_api_key}
            hint={keyHints.openai}
            onChange={(v) => setKeys({ ...keys, openai_api_key: v })}
          />
          <KeyField
            testid="plaud-key-input"
            label="Clé API développeur Plaud"
            placeholder="Votre clé Plaud"
            value={keys.plaud_api_key}
            hint={keyHints.plaud}
            note="Stockée pour préparer l'intégration automatique de Plaud (à venir)."
            onChange={(v) => setKeys({ ...keys, plaud_api_key: v })}
          />
          <KeyField
            testid="calendly-key-input"
            label="Clé API Calendly (Personal Access Token)"
            placeholder="eyJra..."
            value={keys.calendly_api_key}
            hint={keyHints.calendly}
            note="Créez un jeton dans Calendly → Integrations & Apps → API & Webhooks. Permet d'importer vos patients et rendez-vous."
            onChange={(v) => setKeys({ ...keys, calendly_api_key: v })}
          />

          <div className="rounded-xl bg-[#FBF9F6] border border-[#EAE6DF] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#2D2C28]">Gemini via Vertex AI (UE)</p>
                <p className="text-xs text-[#757169] mt-0.5">
                  Configuré côté serveur (compte de service GCP), garde les données en Europe — idéal HDS.
                </p>
              </div>
              {keyHints.vertex?.configured ? (
                <span className="flex items-center gap-1 text-xs text-[#5e7a6e] shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Actif
                </span>
              ) : (
                <span className="text-xs text-[#9c988f] shrink-0">Non configuré</span>
              )}
            </div>
            {keyHints.vertex?.configured && (
              <p className="text-xs text-[#9c988f] mt-2">
                Projet : {keyHints.vertex.project} · Région : {keyHints.vertex.location}
              </p>
            )}
          </div>

          <Button
            onClick={saveKeys}
            disabled={savingKeys}
            data-testid="save-keys-btn"
            className="w-full rounded-xl bg-[#7B968B] hover:bg-[#6a8579] text-white h-11"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingKeys ? "Enregistrement…" : "Enregistrer les clés"}
          </Button>
        </div>

        <EmailTemplatesSection />

        {isAdmin && (
          <>
            <div className="flex items-center gap-3 mt-12">
              <div className="w-11 h-11 rounded-xl bg-[#D4A373]/15 text-[#B7854f] flex items-center justify-center">
                <Users className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-[#2D2C28]">Utilisateurs & accès</h2>
                <p className="text-[#757169] text-sm">
                  Seules les adresses ci-dessous peuvent se connecter. Chaque utilisateur gère ses propres patients.
                </p>
              </div>
            </div>

            <div
              className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)] mt-6 space-y-4"
              data-testid="access-management-section"
            >
              <div className="space-y-2">
                {accessUsers.map((u) => (
                  <div
                    key={u.email}
                    data-testid={`access-user-${u.email}`}
                    className="flex items-center justify-between rounded-xl bg-[#FBF9F6] border border-[#EAE6DF] px-4 py-3"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {u.owner ? (
                        <Crown className="w-4 h-4 text-[#B7854f] shrink-0" strokeWidth={1.5} />
                      ) : (
                        <Users className="w-4 h-4 text-[#7B968B] shrink-0" strokeWidth={1.5} />
                      )}
                      <span className="text-sm text-[#2D2C28] truncate">{u.email}</span>
                      <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${
                        u.role === "admin" ? "bg-[#D4A373]/20 text-[#B7854f]" : "bg-[#7B968B]/15 text-[#5e7a6e]"
                      }`}>
                        {u.owner ? "Propriétaire" : u.role}
                      </span>
                    </div>
                    {!u.owner && (
                      <button
                        onClick={() => removeAccess(u.email)}
                        data-testid={`remove-access-${u.email}`}
                        title="Retirer l'accès"
                        className="p-2 rounded-lg text-[#C0BBB0] hover:text-[#B56B6B] hover:bg-[#B56B6B]/10 transition-colors shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Input
                  data-testid="new-access-email-input"
                  type="email"
                  value={newAccess.email}
                  onChange={(e) => setNewAccess({ ...newAccess, email: e.target.value })}
                  placeholder="collaborateur@email.com"
                  className="bg-white rounded-xl flex-1"
                />
                <Select
                  value={newAccess.role}
                  onValueChange={(v) => setNewAccess({ ...newAccess, role: v })}
                >
                  <SelectTrigger data-testid="new-access-role-select" className="bg-white rounded-xl sm:w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="membre">Membre</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={addAccess}
                  disabled={addingAccess}
                  data-testid="add-access-btn"
                  className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {addingAccess ? "Ajout…" : "Accorder l'accès"}
                </Button>
              </div>
              <p className="text-xs text-[#9c988f]">
                Un « membre » peut tout faire sauf gérer les accès. Retirer un accès déconnecte immédiatement l'utilisateur.
              </p>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

const KeyField = ({ testid, label, placeholder, value, hint, onChange, note }) => (
  <div>
    <div className="flex items-center justify-between">
      <Label className="text-[#575349]">{label}</Label>
      {hint?.configured && (
        <span className="flex items-center gap-1 text-xs text-[#5e7a6e]">
          <CheckCircle2 className="w-3.5 h-3.5" /> Enregistrée ({hint.hint})
        </span>
      )}
    </div>
    <Input
      data-testid={testid}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={hint?.configured ? "Laisser vide pour conserver" : placeholder}
      className="mt-1.5 bg-white rounded-xl"
    />
    {note && <p className="text-xs text-[#9c988f] mt-1.5">{note}</p>}
  </div>
);

export default Settings;
